import {
  EtiquetaDePagina,
  CLASE_TITULO_DE_PAGINA,
  CLASE_SUBTITULO_DE_PAGINA,
} from '@/components/patterns';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FadeIn, StaggerChildren, StaggerItem } from '@/components/motion';
import {
  Shield,
  Database,
  DollarSign,
  FileText,
  CheckSquare,
  Users,
  Bot,
  LayoutDashboard,
  Package,
  BarChart3,
  Bell,
  ClipboardList,
  Server,
  Calendar,
  Target,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  ArrowDown,
  Building2,
  Calculator,
  ThumbsUp,
  RotateCcw,
  Sparkles,
  UserCog,
  UserCheck,
  ShieldCheck,
  ArrowRight,
  TestTubes,
  Send,
  Navigation,
  Brain,
  Gauge,
  ShoppingCart,
  Contact,
  KeyRound,
  Ship,
  Plug,
  FileDown,
  GraduationCap,
  Code,
  BookOpen,
  Coins,
  Globe,
  Store,
  Percent,
  Wand2,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';

// --- Data ---

type ModuleStatus = 'completed' | 'partial' | 'planned';

interface ProjectModule {
  name: string;
  description: string;
  status: ModuleStatus;
  progress: number;
  icon: any;
  features: string[];
  phase: number;
  isNew?: boolean;
}

interface PhaseSubtask {
  name: string;
  status: ModuleStatus;
}

interface PhaseModule {
  name: string;
  startDate: string;
  endDate: string;
  status: ModuleStatus;
  subtasks?: PhaseSubtask[];
}

interface TimelinePhase {
  phase: number;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status: ModuleStatus;
  modules: PhaseModule[];
}

const modules: ProjectModule[] = [
  // --- Phase 1: Fundamentos ---
  {
    name: 'Autenticacion y Seguridad',
    description: 'JWT, refresh tokens, RBAC con 4 roles, multi-tenancy',
    status: 'completed',
    progress: 100,
    icon: Shield,
    features: [
      'Login / Registro',
      'Refresh tokens 24h',
      '4 roles (Platform Admin, Company Admin, Salesperson, Approver)',
      'Aislamiento multi-tenant',
    ],
    phase: 1,
  },
  {
    name: 'Dashboard',
    description: 'Pagina principal con metricas, accesos rapidos y widgets de alertas de mercado',
    status: 'completed',
    progress: 100,
    icon: LayoutDashboard,
    features: [
      'Tarjetas de acceso rapido',
      'Info del usuario',
      'Navegacion rapida',
      'Widget de alertas de mercado',
      'Widget de productividad IA',
    ],
    phase: 1,
  },
  // --- Phase 2: Datos y Configuracion ---
  {
    name: 'Datos Maestros',
    description:
      'Divisas, FX rates historicas, LOBs jerarquicos, impuestos, paises, freight providers/routes con importacion masiva CSV',
    status: 'completed',
    progress: 100,
    icon: Database,
    features: [
      'CRUD Divisas con tabla historica de tipos de cambio (CurrencyExchangeRate)',
      'Endpoint de lookup de tasa por fecha (para Currency Simulator)',
      'LOBs jerarquicos (3 niveles: Sector > Vertical > Producto) con margenes override',
      'Configuracion de impuestos (% y fijo)',
      'Gestion de paises ISO',
      'Freight providers y freight routes (tramos)',
      'Configuracion de Incoterms',
      'Importacion masiva CSV con plantilla descargable (Divisas, LOBs, Impuestos, Paises)',
      'Upsert inteligente por codigo (crea o actualiza)',
      'Preview de primeras 5 filas antes de importar',
      'Reporte de errores por fila tras importacion',
    ],
    phase: 2,
  },
  {
    name: 'Materiales',
    description:
      'Inventario de materiales con historial de aranceles, codigos de proveedor e importacion CSV',
    status: 'completed',
    progress: 100,
    icon: Package,
    features: [
      'CRUD materiales',
      'Codigos externos (proveedor)',
      'Historial de aranceles con vigencia (upsert transaccional)',
      'Partida arancelaria',
      'Importacion masiva CSV (upsert por codigo, soporta lobCode)',
      'Asignacion automatica de LOB por codigo en importacion',
    ],
    phase: 2,
  },
  {
    name: 'Usuarios',
    description: 'Gestion de usuarios, asignacion de roles y activacion/desactivacion',
    status: 'completed',
    progress: 100,
    icon: Users,
    features: [
      'CRUD usuarios',
      'Asignacion de roles',
      'Activar/desactivar',
      'Filtro por rol y busqueda',
    ],
    phase: 2,
  },
  {
    name: 'Clientes',
    description: 'CRM ligero con gestion de clientes, contactos, segmentacion e importacion CSV',
    status: 'completed',
    progress: 100,
    icon: Contact,
    features: [
      'CRUD clientes',
      'Contactos por cliente (nombre, email, cargo, es-principal)',
      'Segmentacion de clientes (ENTERPRISE, SMB, GOBIERNO, STARTUP)',
      'Busqueda y filtros avanzados',
      'Importacion masiva CSV con plantilla descargable',
      'Upsert por codigo (crea o actualiza segun si ya existe)',
    ],
    phase: 2,
  },
  // --- Phase 3: Core Business ---
  {
    name: 'Modelos de Pricing',
    description:
      'Creador de plantillas con motor de formulas, variables tipadas, roles de variable y grafo de dependencias interactivo',
    status: 'partial',
    progress: 80,
    icon: DollarSign,
    features: [
      '9 tipos de variables (Number, %, Currency, Text, Boolean, Calculated, Enum, Tiered, Lookup)',
      'Roles de variable extendidos: COST, PRICE, MARGIN, TAX, FREIGHT, LIST_PRICE',
      'Variables PERCENTAGE auto-divididas entre 100 en formulas',
      'priceRole por variable: define precio unitario o total',
      'Panel "Roles en Cotizacion" en la vista del modelo',
      'Motor de formulas con parser y evaluador en tiempo real',
      'Deteccion automatica de dependencias circulares',
      'Grafo interactivo de dependencias (click para detalles, fullscreen, drag & zoom)',
      'Tablas Lookup con dimensiones fila/columna',
      'Variables escalonadas (Tiered) con rangos de cantidad',
      'Drag & drop para reordenar variables',
      'Clonado de modelos completos',
      'Eliminados margen objetivo/piso del modelo: ahora todo sale de variables con role MARGIN',
      'Pendiente: versionado de modelos con historial de cambios',
      'Pendiente: importar/exportar modelos (JSON/Excel)',
    ],
    phase: 3,
  },
  {
    name: 'Cotizaciones',
    description:
      'Cotizaciones multi-seccion con calculo automatico, waterfall, simulador de divisa, grupos de envio y prorrateo de flete',
    status: 'partial',
    progress: 80,
    icon: FileText,
    features: [
      'Formulario multi-paso (cliente, modelo, divisa, pais, LOB)',
      'Secciones multiples — cada seccion usa un modelo de pricing diferente',
      'Lineas de detalle con materiales y cantidades',
      'Calculo en tiempo real al modificar variables',
      'Cost Waterfall visual (cascada paso a paso del costo al precio)',
      'Indicador de margen con semaforo (verde/naranja/rojo)',
      'Margen real por roles (calculo basado en variables MARGIN/TAX/COST)',
      'Currency Simulator: convertidor de divisas en linea con tipo de cambio configurado',
      'Conversion de divisa en cadena de precios (resolveUpstreamPrices con FX)',
      'Grupos de envio con prorrateo de flete y UX unificada de logistica',
      'Configuracion de freight legs (tramos de flete) durante la creacion',
      'Race condition fix en generacion de codigo de cotizaciones',
      'Estados del ciclo de vida: Draft, Pending, Approved, Rejected, Frozen',
      'Pendiente: duplicar cotizacion como borrador',
      'Pendiente: comparacion lado a lado de versiones',
    ],
    phase: 3,
  },
  {
    name: 'Aprobaciones',
    description:
      'Flujo de aprobacion con cola de pendientes, acciones multiples y notificaciones al vendedor',
    status: 'partial',
    progress: 55,
    icon: CheckSquare,
    features: [
      'Cola de aprobaciones pendientes filtrable',
      'Vista detallada de la cotizacion desde la aprobacion',
      'Tres acciones: Aprobar / Rechazar / Solicitar Ajuste',
      'Comentarios del aprobador en cada accion',
      'Notificacion in-app al vendedor al aprobar/rechazar',
      'Reenvio de cotizacion ajustada al flujo',
      'Pendiente: notificaciones por email al aprobador',
      'Pendiente: escalamiento automatico por tiempo sin respuesta',
      'Pendiente: aprobacion en cadena (multiples aprobadores)',
    ],
    phase: 3,
  },
  // --- Phase 3.5: Nuevos Modulos (Feb 2026) ---
  {
    name: 'Pricing Dinamico (E-Commerce)',
    description:
      'Motor de precios dinamicos con scraping de competidores, inferencia automatica, comparativa VS y alertas',
    status: 'partial',
    progress: 90,
    icon: ShoppingCart,
    features: [
      'CRUD de productos dinamicos con SKU y estrategias de pricing',
      '4 estrategias: Match Lowest, Undercut %, Margin Target, Custom Formula',
      'Gestion de tiendas de competidores con catalogo propio vs competidores',
      'Scraping de catalogos: Dismac, CSV masivo, API REST',
      'Inferencia automatica: matching por SKU, nombre, precio, categoria',
      'Panel "Quien vs Quien" con layout VS y resumen de resultados',
      'Sugerencias de match con aceptar/rechazar y auto-apply',
      'Items sin coincidencia con tabla expandible y best score',
      'Motor de calculo de precios con analisis de competencia',
      'Alertas automaticas de cambio de precio (INFO/WARNING/CRITICAL)',
      'Log de auditoria de acciones de pricing',
      'Tabs: Productos, Competidores, Catalogo, Inferencia, Graficos, Alertas, API',
      'Pendiente: IA en comparativa de productos',
      'Pendiente: reglas de alerta configurables por producto',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Organizaciones',
    description:
      'Soporte multi-organizacion dentro del tenant para segmentar equipos y datos por unidad de negocio',
    status: 'completed',
    progress: 100,
    icon: Building2,
    features: [
      'CRUD de organizaciones por tenant',
      'Gestion de miembros (agregar/quitar usuarios)',
      'Switcher de organizacion en el navbar',
      'Admins ven "Todas las organizaciones"',
      'Middleware de filtrado por organizacion',
      'Constraint unico: tenantId + codigo de org',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'AI Intelligence',
    description:
      'Panel de inteligencia artificial con insights, predicciones, recomendaciones y simulador de escenarios',
    status: 'partial',
    progress: 70,
    icon: Brain,
    features: [
      'Tab de Insights: analisis de datos de pricing',
      'Tab de Predicciones: forecast basado en historico',
      'Tab de Recomendaciones: sugerencias de optimizacion',
      'Tab de Simulador: escenarios what-if interactivos',
      'Selector de cotizacion y modelo para contexto',
      'Endpoints backend con logica de IA',
      'Store Zustand para estado del modulo',
      'Pendiente: integracion con modelos ML avanzados',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Tour Guiado (Onboarding)',
    description:
      'Sistema de tours interactivos, wizard de registro multi-paso y modal de onboarding por segmento',
    status: 'completed',
    progress: 100,
    icon: Navigation,
    features: [
      'Tour Provider global con overlay y tooltips',
      'Tours especificos: Dashboard, Modelos, Cotizaciones, Aprobaciones, AI',
      'Resaltado visual del elemento objetivo con navegacion paso a paso',
      'Boton flotante (FAB) para reiniciar tours desde cualquier pagina',
      'Estado persistido en Zustand (tours completados)',
      'Wizard de registro multi-paso (3 pasos: negocio → empresa → cuenta)',
      'Seleccion de segmento de industria en registro (retail, b2b, tech, servicios)',
      'Modal de bienvenida post-registro con onboarding contextual por segmento',
      'Tutorial adaptado por segmento con personas y casos de uso reales',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'AI Productividad',
    description:
      'Dashboard gamificado que muestra el ROI y ahorro de tiempo al usar funcionalidades de IA vs procesos manuales',
    status: 'completed',
    progress: 100,
    icon: Gauge,
    features: [
      'Contadores de "desperdicio" (tiempo/costo manual vs IA)',
      'Tarjetas de escenarios comparativos',
      'Timer en tiempo real de ahorro acumulado',
      'Pulso de productividad general',
      'Datos de ratios de productividad configurables',
      'Visualizacion gamificada e interactiva',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Permisos RBAC Granulares',
    description:
      'Permisos por rol configurable a nivel de tenant, custom roles, support mode (impersonation) y visibilidad de menu',
    status: 'partial',
    progress: 80,
    icon: KeyRound,
    features: [
      'API de configuracion de permisos por rol',
      'Mapeo role-permission por tenant (tabla RolePermission)',
      'Custom roles definidos por tenant (CustomRole)',
      'Middleware de validacion de permisos en cada endpoint',
      'Visibilidad de items de menu por rol (menu:* keys)',
      'DEFAULT_MENU_VISIBILITY por rol: COMPANY_ADMIN ve todos los modulos',
      'canSeeMenu() en frontend sincronizado con permisos del servidor',
      'Support Mode: PLATFORM_ADMIN puede impersonar usuarios para soporte',
      'Pendiente: UI de matriz de permisos para Company Admin',
      'Pendiente: permisos a nivel de organizacion',
    ],
    phase: 3,
    isNew: true,
  },
  // --- Phase 3.5 continued: Nuevos Modulos (Mar 2026) ---
  {
    name: 'Importaciones (Supply Chain)',
    description:
      'Sistema completo de tracking de importaciones con ordenes, contenedores 3D, landed cost y pipeline logistico',
    status: 'completed',
    progress: 100,
    icon: Ship,
    features: [
      'CRUD de ordenes, items, proveedores y tipos de contenedor',
      'Contenedores con visualizacion 3D interactiva (React Three Fiber)',
      'Algoritmo de bin-packing para optimizar carga de contenedores',
      'Calculadora de landed cost (arancel, flete, seguro, impuestos)',
      'Auto-calculo masivo de items + sugerencia de contenedores',
      'Pipeline board (Kanban) con 8 columnas de estado',
      'Timeline de tracking con eventos logisticos',
      'Documentos adjuntos (BL, factura, packing list)',
      'Dashboard con KPIs, tendencias mensuales y top proveedores',
      'Mapa de disponibilidad de contenedores por puerto',
      'Log de auditoria completo de todos los cambios',
      '43 endpoints backend + 29 componentes frontend',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Integracion SAP',
    description:
      'Conectividad con SAP C4C para sincronizacion de materiales, clientes y datos maestros via OData',
    status: 'partial',
    progress: 75,
    icon: Plug,
    features: [
      'Cliente OData generico para APIs SAP',
      'Sincronizacion de materiales desde SAP (material master)',
      'Sincronizacion CRM desde SAP C4C (clientes y contactos)',
      'Job BullMQ de sync cada 5 minutos',
      'Modelo IntegrationConfig para credenciales SAP por tenant',
      'Modelo SyncLog para registro de sincronizaciones',
      'Pendiente: sync bidireccional (push cotizaciones a SAP)',
      'Pendiente: mapeo configurable de campos SAP ↔ sistema',
    ],
    phase: 4,
    isNew: true,
  },
  {
    name: 'Portal de Desarrollador',
    description:
      'Documentacion de API REST interactiva y gestion de API keys para integraciones externas',
    status: 'partial',
    progress: 80,
    icon: Code,
    features: [
      'Pagina de documentacion de API (/developer)',
      'CRUD de API keys con permisos configurables',
      'Generacion y revocacion de tokens',
      'Endpoint v1/pricer como API publica de pricing',
      'Acceso restringido a COMPANY_ADMIN y PLATFORM_ADMIN',
      'Pendiente: sandbox interactivo para probar endpoints',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Entrenamiento de Ventas',
    description:
      'Modulo de capacitacion con perfiles de vendedor, ejemplos de negociacion y mejores practicas',
    status: 'partial',
    progress: 50,
    icon: GraduationCap,
    features: [
      'Perfiles de vendedor con metricas de rendimiento',
      'Ejemplos de ventas exitosas categorizados',
      'Contenido de capacitacion por segmento',
      'Endpoints backend para perfiles y ejemplos',
      'Pendiente: sistema de certificaciones',
      'Pendiente: quizzes interactivos',
    ],
    phase: 4,
    isNew: true,
  },
  {
    name: 'Generacion de PDF',
    description:
      'Exportacion de cotizaciones a PDF profesional con plantilla personalizada y calculo de totales',
    status: 'partial',
    progress: 70,
    icon: FileDown,
    features: [
      'Servicio de generacion de PDF backend',
      'Endpoint /api/pdf para descargar cotizacion',
      'Plantilla con datos del cliente, items, totales e impuestos',
      'Pendiente: hash inmutable para verificacion de integridad',
      'Pendiente: plantillas personalizables por tenant',
    ],
    phase: 4,
    isNew: true,
  },
  // --- Phase 3.6: Smart Pricing & Comercio (Abr 2026) ---
  {
    name: 'Smart Pricing (Precio Inteligente)',
    description:
      'Motor de pricing inteligente con 5 estrategias, simulacion, comparacion por competidor y aplicacion masiva',
    status: 'completed',
    progress: 95,
    icon: Wand2,
    features: [
      '5 estrategias de pricing con descripciones detalladas para el cliente',
      'Simulador interactivo con parametros configurables por estrategia',
      'Analisis de catalogo sin competidores: recomienda precios por margen optimo segun rango de costo',
      'Comparacion por competidor con selector de tienda y tabla de matches por inferencia',
      'Pestana "Productos vinculados" con persistencia',
      'Aplicar estrategia a productos desde Precio Inteligente',
      'Aplicar precios y push automatico a tiendas conectadas (sin limite de productos)',
      'Tarjetas de resumen clickeables en Analisis de Catalogo',
      'Filtros por cards funcionales sin duplicar productos',
      'Export a Excel + estimacion de impacto + historial por producto',
      'Optimizacion de runInference con inverted index y batching',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Reglas y Descuentos de Pricing',
    description:
      'Wizards para definir reglas de pricing y descuentos aplicables a tiendas conectadas',
    status: 'partial',
    progress: 75,
    icon: Percent,
    features: [
      'Wizard scrolleable para crear reglas de pricing',
      'Wizard de descuentos con condiciones y vigencia',
      'Boton "Aplicar a tienda" desde Smart Pricing, Reglas y Descuentos',
      'Endpoint /api/v1/price/:sku con info de descuento activo',
      'Pendiente: editor visual avanzado de condiciones',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Tipos de Cambio y Currency Simulator',
    description:
      'Tabla historica de tipos de cambio por divisa, conversion en cadena de precios y simulador en cotizaciones',
    status: 'completed',
    progress: 100,
    icon: Coins,
    features: [
      'Tabla CurrencyExchangeRate con FX historicos por divisa y fecha',
      'Migracion 20260423_add_currency_exchange_rates aplicada en GCP VM',
      'Endpoint de lookup de tasa por divisa+fecha',
      'Currency Simulator embebido en quote-detail (Financial Summary)',
      'resolveUpstreamPrices con currency conversion automatica',
      'Warning visual cuando no hay tasa para la fecha de la cotizacion',
      'formatCurrency con fallback seguro para codigos no-ISO 4217 (USDC, BS)',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Store Connections',
    description:
      'Conexion bidireccional con tiendas externas: push de precios, sync de catalogo, historial y revert',
    status: 'completed',
    progress: 90,
    icon: Globe,
    features: [
      'CRUD de tiendas conectadas (TechStore, Shopify, etc.)',
      'Push de precios con history y revert a estado anterior',
      'Sync de catalogo en lotes de 500 productos',
      'Test de conexion con tiendas',
      'Aplicar precios automaticamente a tiendas conectadas',
      'costPrice sync corregido sin limite de productos',
      'validateStoreSecret protegiendo /prices/log y /prices/apply-from-api',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'Demo Store (TechStore)',
    description:
      'E-commerce demo full-stack para probar la integracion API en vivo: storefront + admin + 3000 productos',
    status: 'completed',
    progress: 100,
    icon: Store,
    features: [
      'React + Vite scaffold con layout y routing',
      'Storefront UI: catalogo, detalle de producto, carrito, checkout',
      'Admin panel: dashboard, settings, sync, price log',
      'Seed de 3000 productos electronicos inspirados en Dismac/Multicenter',
      'API routes: product, cart, price-push y sync',
      'Pagina de integracion en TechStore con 3 pasos: sync, consultar, aplicar',
      'Guia visual en PI para conectar la tienda',
      'CORS configurado para puertos de desarrollo (3000-3003)',
      'Incluido en npm start',
    ],
    phase: 3,
    isNew: true,
  },
  {
    name: 'API v1 Production-Ready',
    description:
      'API publica de pricing endurecida con cache Redis, rate limiting, circuit breaker, Swagger y autenticacion por API Key',
    status: 'completed',
    progress: 95,
    icon: Server,
    features: [
      'Cache Redis con TTL configurable',
      'Rate limiting por API key',
      'Circuit breaker para evitar cascadas',
      'Documentacion Swagger generada',
      'Base URL configurable',
      'Autenticacion por API Key para endpoints de ingesta',
      'Tabla de keys con auto-refresh tras cada test exitoso',
      'Pagina de gestion /claves-api (renombrada para evitar conflicto con proxy Vite)',
      'Guia visual "Como usar" con pasos en la pagina de API Keys',
    ],
    phase: 4,
    isNew: true,
  },
  {
    name: 'Help Center y Manual',
    description:
      'Centro de ayuda con manual completo, FAQ, ilustraciones por capitulo y reorganizacion por modulos del sistema',
    status: 'completed',
    progress: 100,
    icon: BookOpen,
    features: [
      'Ruta /help con manual y FAQ',
      'Manual y FAQ de Pricing Dinamico',
      'Capitulos: Smart Pricing, Descuentos, Reglas de Pricing, Clientes, Importaciones',
      'Modulos: Integraciones, API Keys',
      'Ilustraciones visuales en todos los capitulos (10-16 Pricing Dinamico, etc.)',
      'Callouts y tutoriales paso a paso',
      'Reorganizacion como sidebar del sistema con renumeracion de capitulos',
      'Scroll automatico al seleccionar capitulo',
    ],
    phase: 4,
    isNew: true,
  },
  {
    name: 'Navegacion y UX Global',
    description:
      'Breadcrumbs dinamicos, busqueda global con Ctrl+K, sidebar reorganizado por flujo y validaciones de formularios',
    status: 'completed',
    progress: 100,
    icon: Search,
    features: [
      'Breadcrumbs dinamicos en el header',
      'Busqueda global con shortcut Ctrl+K',
      'Sidebar reorganizado por flujo logico con separadores por seccion',
      'Validacion de email y password al crear usuarios',
      'Validacion de email en clientes y contactos',
      'Validacion de caracteres y largo minimo en perfil (nombre/apellido)',
    ],
    phase: 4,
    isNew: true,
  },
  // --- Phase 4: Mejoras ---
  {
    name: 'Asistente IA',
    description:
      'Chat contextual con IA integrado en la plataforma con tool use para consultar datos reales del sistema',
    status: 'partial',
    progress: 60,
    icon: Bot,
    features: [
      'Widget flotante de chat accesible desde cualquier pagina',
      'Preguntas rapidas predefinidas para onboarding',
      'Respuestas en espanol con conocimiento del dominio de pricing',
      'Contexto inyectado: roles, modelos, flujos del sistema',
      'Tool use: consulta cotizaciones, modelos, materiales en tiempo real',
      'Definiciones de herramientas y handlers para datos del dominio',
      'Fallback a servicio mock cuando no hay API key',
      'Pendiente: historial de conversaciones persistente',
      'Pendiente: sugerencias contextuales segun la pagina actual',
      'Pendiente: acciones desde el chat (crear cotizacion, buscar material)',
    ],
    phase: 4,
  },
  {
    name: 'Analiticas Avanzadas',
    description:
      'Dashboard de analiticas con rendimiento de cotizaciones, tendencias de margen y metricas de conversion',
    status: 'partial',
    progress: 35,
    icon: BarChart3,
    features: [
      'Endpoints de rendimiento de cotizaciones',
      'Tendencias de margen',
      'Tasas de conversion',
      'Pagina frontend con graficos',
      'Pendiente: metricas avanzadas de aprobacion',
      'Pendiente: forecasting de ingresos',
    ],
    phase: 4,
  },
  {
    name: 'Notificaciones',
    description: 'Centro de notificaciones in-app con marcado de lectura y endpoints de gestion',
    status: 'partial',
    progress: 40,
    icon: Bell,
    features: [
      'API de notificaciones (GET, marcar como leida)',
      'Modelo de BD para notificaciones',
      'Generacion automatica en eventos de aprobacion',
      'Pendiente: centro de notificaciones en UI',
      'Pendiente: notificaciones por email',
      'Pendiente: webhooks para sistemas externos',
    ],
    phase: 4,
  },
  {
    name: 'Auditoria',
    description: 'Log completo de cambios para compliance',
    status: 'planned',
    progress: 0,
    icon: ClipboardList,
    features: [
      'Historial de cambios por entidad',
      'Quien cambio que y cuando',
      'Reportes de cumplimiento',
    ],
    phase: 4,
  },
  {
    name: 'Telegram Bot (IA)',
    description: 'Integracion del asistente IA con Telegram para conversar desde el movil',
    status: 'planned',
    progress: 0,
    icon: Send,
    features: [
      'Bot de Telegram conectado a la API de IA',
      'Consultar precios, margenes y estado de cotizaciones',
      'Recibir notificaciones de aprobaciones pendientes',
      'Autenticacion por token vinculado al usuario',
    ],
    phase: 4,
  },
  // --- Phase 5: QA ---
  {
    name: 'QA y Testing',
    description: 'Suite de pruebas automatizadas, testing E2E y validacion de calidad',
    status: 'planned',
    progress: 0,
    icon: TestTubes,
    features: [
      'Tests unitarios backend (Vitest)',
      'Tests de integracion de API endpoints',
      'Tests E2E del frontend (Playwright)',
      'Cobertura minima objetivo: 80%',
    ],
    phase: 5,
  },
];

const phases: TimelinePhase[] = [
  {
    phase: 1,
    name: 'Fundamentos',
    description: 'Infraestructura, autenticacion y layout base',
    startDate: '2026-01-15',
    endDate: '2026-01-31',
    status: 'completed',
    modules: [
      {
        name: 'Autenticacion y Seguridad',
        startDate: '2026-01-15',
        endDate: '2026-01-22',
        status: 'completed',
      },
      { name: 'Dashboard', startDate: '2026-01-22', endDate: '2026-01-31', status: 'completed' },
    ],
  },
  {
    phase: 2,
    name: 'Datos y Configuracion',
    description: 'Datos maestros, materiales, usuarios y clientes',
    startDate: '2026-02-01',
    endDate: '2026-02-10',
    status: 'completed',
    modules: [
      {
        name: 'Datos Maestros',
        startDate: '2026-02-01',
        endDate: '2026-02-04',
        status: 'completed',
      },
      { name: 'Materiales', startDate: '2026-02-03', endDate: '2026-02-06', status: 'completed' },
      { name: 'Usuarios', startDate: '2026-02-05', endDate: '2026-02-07', status: 'completed' },
      { name: 'Clientes', startDate: '2026-02-06', endDate: '2026-02-10', status: 'completed' },
    ],
  },
  {
    phase: 3,
    name: 'Core Business',
    description: 'Cotizaciones, aprobaciones y modelos de pricing',
    startDate: '2026-02-11',
    endDate: '2026-02-23',
    status: 'completed',
    modules: [
      {
        name: 'Modelos de Pricing',
        startDate: '2026-02-11',
        endDate: '2026-02-17',
        status: 'completed',
      },
      { name: 'Cotizaciones', startDate: '2026-02-14', endDate: '2026-02-19', status: 'completed' },
      { name: 'Aprobaciones', startDate: '2026-02-18', endDate: '2026-02-23', status: 'completed' },
    ],
  },
  {
    phase: 3.5,
    name: 'Nuevos Modulos',
    description:
      'Pricing dinamico, organizaciones, AI intelligence, tours, importaciones, SAP, developer portal, permisos y mas',
    startDate: '2026-02-20',
    endDate: '2026-03-20',
    status: 'partial',
    modules: [
      {
        name: 'Pricing Dinamico (E-Commerce)',
        startDate: '2026-02-20',
        endDate: '2026-03-10',
        status: 'partial',
        subtasks: [
          { name: 'Motor de pricing y estrategias', status: 'completed' },
          { name: 'Scraping de catalogos (Dismac, CSV, API)', status: 'completed' },
          { name: 'Inferencia automatica (matching)', status: 'completed' },
          { name: 'Asociacion manual y sugerencias', status: 'completed' },
          { name: 'IA en comparativa de productos', status: 'partial' },
          { name: 'Dashboard comparativo VS', status: 'completed' },
        ],
      },
      {
        name: 'Organizaciones',
        startDate: '2026-02-22',
        endDate: '2026-02-24',
        status: 'completed',
      },
      {
        name: 'AI Intelligence',
        startDate: '2026-02-23',
        endDate: '2026-02-27',
        status: 'partial',
      },
      {
        name: 'Tour Guiado (Onboarding)',
        startDate: '2026-02-25',
        endDate: '2026-02-28',
        status: 'completed',
      },
      {
        name: 'AI Productividad',
        startDate: '2026-02-25',
        endDate: '2026-02-27',
        status: 'completed',
      },
      {
        name: 'Permisos RBAC Granulares',
        startDate: '2026-02-27',
        endDate: '2026-03-02',
        status: 'partial',
      },
      {
        name: 'Onboarding Wizard (Registro)',
        startDate: '2026-02-28',
        endDate: '2026-03-02',
        status: 'completed',
      },
      {
        name: 'Alertas de Pricing',
        startDate: '2026-03-03',
        endDate: '2026-03-08',
        status: 'partial',
        subtasks: [
          { name: 'Alertas automaticas por cambio de precio', status: 'completed' },
          { name: 'Panel de alertas con filtros y severidad', status: 'completed' },
          { name: 'Notificaciones in-app de alertas criticas', status: 'partial' },
          { name: 'Reglas de alerta configurables por producto', status: 'planned' },
        ],
      },
      {
        name: 'Importacion CSV Masiva',
        startDate: '2026-03-01',
        endDate: '2026-03-03',
        status: 'completed',
      },
      {
        name: 'API Keys para integraciones',
        startDate: '2026-03-05',
        endDate: '2026-03-08',
        status: 'partial',
      },
      {
        name: 'Importaciones (Supply Chain)',
        startDate: '2026-03-10',
        endDate: '2026-03-18',
        status: 'completed',
        subtasks: [
          { name: 'Ordenes, items, proveedores y contenedores', status: 'completed' },
          { name: 'Visualizacion 3D y bin-packing de contenedores', status: 'completed' },
          { name: 'Landed cost calculator y auto-calculo masivo', status: 'completed' },
          { name: 'Pipeline board, tracking, documentos y auditoria', status: 'completed' },
          { name: 'Dashboard KPIs y mapa de disponibilidad', status: 'completed' },
        ],
      },
      {
        name: 'Portal de Desarrollador',
        startDate: '2026-03-08',
        endDate: '2026-03-12',
        status: 'partial',
      },
    ],
  },
  {
    phase: 4,
    name: 'Mejoras e Integraciones',
    description:
      'SAP, asistente IA, PDF, sales training, analiticas, notificaciones, auditoria y Telegram',
    startDate: '2026-03-15',
    endDate: '2026-04-05',
    status: 'partial',
    modules: [
      {
        name: 'Integracion SAP',
        startDate: '2026-03-13',
        endDate: '2026-03-20',
        status: 'partial',
        subtasks: [
          { name: 'Cliente OData generico', status: 'completed' },
          { name: 'Sync de materiales desde SAP', status: 'completed' },
          { name: 'Sync CRM desde SAP C4C', status: 'completed' },
          { name: 'Job BullMQ automatico cada 5 min', status: 'completed' },
          { name: 'Sync bidireccional a SAP', status: 'planned' },
        ],
      },
      {
        name: 'Generacion de PDF',
        startDate: '2026-03-14',
        endDate: '2026-03-18',
        status: 'partial',
      },
      {
        name: 'Entrenamiento de Ventas',
        startDate: '2026-03-15',
        endDate: '2026-03-20',
        status: 'partial',
      },
      { name: 'Asistente IA', startDate: '2026-03-15', endDate: '2026-03-19', status: 'partial' },
      {
        name: 'Analiticas Avanzadas',
        startDate: '2026-03-17',
        endDate: '2026-03-21',
        status: 'partial',
      },
      { name: 'Notificaciones', startDate: '2026-03-20', endDate: '2026-03-23', status: 'partial' },
      { name: 'Auditoria', startDate: '2026-03-23', endDate: '2026-04-05', status: 'planned' },
      {
        name: 'Telegram Bot (IA)',
        startDate: '2026-03-25',
        endDate: '2026-03-28',
        status: 'planned',
      },
    ],
  },
  {
    phase: 4.5,
    name: 'Smart Pricing y Comercio',
    description:
      'Smart Pricing inteligente, demo store, store connections, API v1 production-ready, reglas y descuentos',
    startDate: '2026-04-08',
    endDate: '2026-04-21',
    status: 'completed',
    modules: [
      {
        name: 'Smart Pricing (Precio Inteligente)',
        startDate: '2026-04-08',
        endDate: '2026-04-17',
        status: 'completed',
        subtasks: [
          { name: 'Comparacion por competidor con selector y matches', status: 'completed' },
          { name: '5 estrategias con simulacion y descripciones', status: 'completed' },
          { name: 'Analisis de catalogo sin competidores (margen optimo)', status: 'completed' },
          { name: 'Aplicar precios a tiendas conectadas', status: 'completed' },
          { name: 'Export Excel + impacto + historial por producto', status: 'completed' },
          { name: 'Optimizacion runInference (inverted index + batching)', status: 'completed' },
        ],
      },
      {
        name: 'Reglas y Descuentos de Pricing',
        startDate: '2026-04-10',
        endDate: '2026-04-13',
        status: 'partial',
      },
      {
        name: 'Roles de variable (MARGIN/TAX) y panel en cotizacion',
        startDate: '2026-04-09',
        endDate: '2026-04-13',
        status: 'completed',
      },
      {
        name: 'Freight legs en cotizacion + grupos de envio',
        startDate: '2026-04-09',
        endDate: '2026-04-16',
        status: 'completed',
      },
      {
        name: 'Demo Store (TechStore)',
        startDate: '2026-04-15',
        endDate: '2026-04-17',
        status: 'completed',
        subtasks: [
          { name: 'Storefront UI (catalogo, carrito, checkout)', status: 'completed' },
          { name: 'Admin panel (dashboard, settings, sync, log)', status: 'completed' },
          { name: 'Seed de 3000 productos electronicos', status: 'completed' },
          { name: 'API: product, cart, price-push, sync', status: 'completed' },
          { name: 'Pagina de integracion en 3 pasos', status: 'completed' },
        ],
      },
      {
        name: 'Store Connections (push prices + history)',
        startDate: '2026-04-15',
        endDate: '2026-04-18',
        status: 'completed',
      },
      {
        name: 'API v1 Production-Ready (cache, rate limit, circuit breaker, Swagger)',
        startDate: '2026-04-13',
        endDate: '2026-04-15',
        status: 'completed',
      },
      {
        name: 'Help Center con ilustraciones y modulos',
        startDate: '2026-04-20',
        endDate: '2026-04-21',
        status: 'completed',
      },
      {
        name: 'Breadcrumbs + busqueda global Ctrl+K',
        startDate: '2026-04-13',
        endDate: '2026-04-13',
        status: 'completed',
      },
      {
        name: 'Support Mode (impersonation) PLATFORM_ADMIN',
        startDate: '2026-04-09',
        endDate: '2026-04-09',
        status: 'completed',
      },
    ],
  },
  {
    phase: 4.7,
    name: 'Tipos de Cambio y Currency Simulator',
    description:
      'FX rates historicos, conversion en cadena de precios, simulador en cotizaciones y fix dashboard',
    startDate: '2026-04-23',
    endDate: '2026-04-27',
    status: 'completed',
    modules: [
      {
        name: 'Tipos de Cambio por divisa (FX historico)',
        startDate: '2026-04-23',
        endDate: '2026-04-23',
        status: 'completed',
        subtasks: [
          { name: 'Tabla CurrencyExchangeRate + migracion en GCP VM', status: 'completed' },
          { name: 'Endpoint de lookup por divisa+fecha', status: 'completed' },
          { name: 'resolveUpstreamPrices con currency conversion', status: 'completed' },
        ],
      },
      {
        name: 'Currency Simulator en quote-detail',
        startDate: '2026-04-23',
        endDate: '2026-04-24',
        status: 'completed',
      },
      {
        name: 'Margen real por roles',
        startDate: '2026-04-23',
        endDate: '2026-04-23',
        status: 'completed',
      },
      {
        name: 'Fix arancel sobre costo + flete (no solo CIF)',
        startDate: '2026-04-23',
        endDate: '2026-04-23',
        status: 'completed',
      },
      {
        name: 'Refactor logistics section',
        startDate: '2026-04-23',
        endDate: '2026-04-23',
        status: 'completed',
      },
      {
        name: 'Fix formatCurrency con codigos no-ISO 4217 (USDC)',
        startDate: '2026-04-27',
        endDate: '2026-04-27',
        status: 'completed',
      },
      {
        name: 'Pricer migration plan (Excel Datec)',
        startDate: '2026-04-23',
        endDate: '2026-04-24',
        status: 'partial',
      },
    ],
  },
  {
    phase: 5,
    name: 'QA y Testing',
    description: 'Pruebas automatizadas, testing E2E, correccion de bugs y validacion de calidad',
    startDate: '2026-04-28',
    endDate: '2026-05-08',
    status: 'planned',
    modules: [
      {
        name: 'Tests unitarios backend (Vitest)',
        startDate: '2026-04-28',
        endDate: '2026-05-02',
        status: 'planned',
      },
      {
        name: 'Tests de integracion de API',
        startDate: '2026-04-30',
        endDate: '2026-05-04',
        status: 'planned',
      },
      {
        name: 'Tests E2E frontend (Playwright)',
        startDate: '2026-05-03',
        endDate: '2026-05-08',
        status: 'planned',
      },
    ],
  },
  {
    phase: 6,
    name: 'Soft Launch',
    description: 'Despliegue controlado con grupo reducido de usuarios piloto para validacion real',
    startDate: '2026-05-09',
    endDate: '2026-05-23',
    status: 'planned',
    modules: [
      { name: 'Deploy staging', startDate: '2026-05-09', endDate: '2026-05-11', status: 'planned' },
      {
        name: 'Usuarios piloto',
        startDate: '2026-05-11',
        endDate: '2026-05-18',
        status: 'planned',
      },
      {
        name: 'Feedback y ajustes',
        startDate: '2026-05-18',
        endDate: '2026-05-23',
        status: 'planned',
      },
    ],
  },
  {
    phase: 7,
    name: 'Produccion',
    description: 'Puesta en produccion, monitoreo, soporte y estabilizacion',
    startDate: '2026-05-24',
    endDate: '2026-06-07',
    status: 'planned',
    modules: [
      {
        name: 'Deploy produccion',
        startDate: '2026-05-24',
        endDate: '2026-05-26',
        status: 'planned',
      },
      {
        name: 'Monitoreo activo',
        startDate: '2026-05-26',
        endDate: '2026-06-02',
        status: 'planned',
      },
      { name: 'Soporte L1/L2', startDate: '2026-06-02', endDate: '2026-06-07', status: 'planned' },
      {
        name: 'Documentacion final',
        startDate: '2026-06-03',
        endDate: '2026-06-07',
        status: 'planned',
      },
    ],
  },
];

const techStack = [
  {
    category: 'Frontend',
    items: [
      'React 18',
      'TypeScript 5.7',
      'Vite 6',
      'Tailwind CSS',
      'Zustand',
      'React Hook Form + Zod',
      'Radix UI',
      'Framer Motion',
    ],
  },
  {
    category: 'Backend',
    items: [
      'Express 4.21',
      'TypeScript 5.7',
      'Prisma 5.22',
      'JWT Auth',
      'Zod Validation',
      'Winston Logger',
      'Redis Cache',
    ],
  },
  {
    category: 'Infraestructura',
    items: ['PostgreSQL 16', 'Redis 7', 'Docker Compose', 'Node 20+', 'Adminer'],
  },
  {
    category: 'AI & Integraciones',
    items: [
      'Claude API (Anthropic)',
      'Tool Use / Function Calling',
      'SAP C4C (OData)',
      'BullMQ Workers',
      'Contexto de dominio',
      'Fallback mock',
    ],
  },
  { category: 'QA', items: ['Vitest', 'Playwright (E2E)', 'TestSprite'] },
];

// --- Helpers ---

function getStatusConfig(status: ModuleStatus) {
  switch (status) {
    case 'completed':
      return {
        label: 'Completado',
        variant: 'default' as const,
        color: 'text-green-500',
        bg: 'bg-green-500',
        icon: CheckCircle2,
      };
    case 'partial':
      return {
        label: 'En Progreso',
        variant: 'secondary' as const,
        color: 'text-yellow-500',
        bg: 'bg-yellow-500',
        icon: Clock,
      };
    case 'planned':
      return {
        label: 'Planificado',
        variant: 'outline' as const,
        color: 'text-muted-foreground',
        bg: 'bg-muted-foreground',
        icon: Circle,
      };
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });
}

// --- Components ---

function ProgressBar({ value, size = 'md' }: { value: number; size?: 'sm' | 'md' | 'lg' }) {
  const heights = { sm: 'h-1.5', md: 'h-2.5', lg: 'h-4' };
  return (
    <div className={cn('w-full rounded-full bg-muted overflow-hidden', heights[size])}>
      <div
        className={cn(
          'h-full rounded-full transition-all duration-700 ease-out',
          value === 100 ? 'bg-green-500' : value > 0 ? 'bg-yellow-500' : 'bg-muted-foreground/30'
        )}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string | number;
  icon: any;
  accent?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-3xl font-bold mt-1', accent)}>{value}</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ModuleCard({ mod }: { mod: ProjectModule }) {
  const config = getStatusConfig(mod.status);
  const Icon = mod.icon;
  const StatusIcon = config.icon;

  return (
    <Card className={cn('h-full', mod.isNew && 'ring-2 ring-primary/30')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                mod.status === 'completed' ? 'bg-green-500/10' : 'bg-muted'
              )}
            >
              <Icon
                className={cn(
                  'w-5 h-5',
                  mod.status === 'completed' ? 'text-green-500' : 'text-muted-foreground'
                )}
              />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {mod.name}
                {mod.isNew && (
                  <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-primary">
                    NUEVO
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">{mod.description}</CardDescription>
            </div>
          </div>
          <Badge variant={config.variant} className="flex items-center gap-1 shrink-0">
            <StatusIcon className="w-3 h-3" />
            {config.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <ProgressBar value={mod.progress} size="sm" />
          <span className="text-xs font-medium text-muted-foreground w-10 text-right">
            {mod.progress}%
          </span>
        </div>
        <ul className="space-y-1">
          {mod.features.map((f, i) => (
            <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
              <span
                className={cn(
                  'mt-1 w-1.5 h-1.5 rounded-full shrink-0',
                  f.startsWith('Pendiente')
                    ? 'bg-muted-foreground/40'
                    : mod.status === 'completed'
                      ? 'bg-green-500'
                      : 'bg-yellow-500'
                )}
              />
              {f}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function TimelineItem({ phase: p, isLast }: { phase: TimelinePhase; isLast: boolean }) {
  const config = getStatusConfig(p.status);
  const StatusIcon = config.icon;

  return (
    <div className="flex gap-4">
      {/* Timeline line + dot */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0',
            p.status === 'completed'
              ? 'border-green-500 bg-green-500/10'
              : p.status === 'partial'
                ? 'border-yellow-500 bg-yellow-500/10'
                : 'border-muted-foreground/30 bg-background'
          )}
        >
          <StatusIcon className={cn('w-4 h-4', config.color)} />
        </div>
        {!isLast && (
          <div
            className={cn(
              'w-0.5 flex-1 min-h-[2rem]',
              p.status === 'completed'
                ? 'bg-green-500/50'
                : p.status === 'partial'
                  ? 'bg-yellow-500/50'
                  : 'bg-muted-foreground/20'
            )}
          />
        )}
      </div>

      {/* Content */}
      <div className="pb-8 flex-1">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-sm font-bold">
            Fase {p.phase}: {p.name}
          </span>
          <Badge variant={config.variant} className="text-xs">
            {config.label}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-2">{p.description}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <Calendar className="w-3 h-3" />
          <span>
            {formatDate(p.startDate)} — {formatDate(p.endDate)}
          </span>
        </div>

        {/* Módulos con fechas individuales */}
        <div className="space-y-1.5 border-l-2 border-muted pl-3 ml-1">
          {p.modules.map((m) => {
            const mc = getStatusConfig(m.status);
            const MIcon = mc.icon;
            return (
              <div key={m.name}>
                <div className="flex items-center gap-2 min-w-0">
                  <MIcon className={cn('w-3 h-3 shrink-0', mc.color)} />
                  <span className="text-xs font-medium truncate flex-1">{m.name}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                    {formatDate(m.startDate)} — {formatDate(m.endDate)}
                  </span>
                </div>
                {m.subtasks && m.subtasks.length > 0 && (
                  <div className="ml-5 mt-1 mb-1.5 space-y-0.5 border-l border-muted-foreground/20 pl-2.5">
                    {m.subtasks.map((st) => {
                      const sc = getStatusConfig(st.status);
                      const SIcon = sc.icon;
                      return (
                        <div key={st.name} className="flex items-center gap-1.5">
                          <SIcon className={cn('w-2.5 h-2.5 shrink-0', sc.color)} />
                          <span className="text-[11px] text-muted-foreground">{st.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- User Flow Data ---

type FlowRole =
  | 'platform_admin'
  | 'company_admin'
  | 'salesperson'
  | 'approver'
  | 'system'
  | 'anyone';

interface FlowStep {
  step: number;
  title: string;
  description: string;
  role: FlowRole;
  icon: any;
  module: string;
  detail: string[];
  outcome?: string;
  branch?: { condition: string; yes: string; no: string };
}

const roleConfig: Record<FlowRole, { label: string; color: string; bg: string; border: string }> = {
  platform_admin: {
    label: 'Platform Admin',
    color: 'text-red-600',
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-900',
  },
  company_admin: {
    label: 'Company Admin',
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-900',
  },
  salesperson: {
    label: 'Vendedor',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    border: 'border-emerald-200 dark:border-emerald-900',
  },
  approver: {
    label: 'Aprobador',
    color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-900',
  },
  system: {
    label: 'Sistema',
    color: 'text-purple-600',
    bg: 'bg-purple-50 dark:bg-purple-950/30',
    border: 'border-purple-200 dark:border-purple-900',
  },
  anyone: {
    label: 'Cualquier rol',
    color: 'text-gray-600',
    bg: 'bg-gray-50 dark:bg-gray-950/30',
    border: 'border-gray-200 dark:border-gray-800',
  },
};

const userFlowSteps: FlowStep[] = [
  {
    step: 1,
    title: 'Registro de la empresa',
    description:
      'Una empresa nueva se registra en la plataforma creando su tenant y usuario administrador.',
    role: 'platform_admin',
    icon: Building2,
    module: '/register',
    detail: [
      'Ingresa nombre de empresa, NIT/Tax ID',
      'Crea el primer usuario (Company Admin)',
      'Se genera el tenant aislado automaticamente',
    ],
    outcome: 'Tenant creado con datos de empresa y admin listo para ingresar',
  },
  {
    step: 2,
    title: 'Configurar datos maestros',
    description: 'El administrador configura la informacion base que usara toda la empresa.',
    role: 'company_admin',
    icon: Database,
    module: '/master-data',
    detail: [
      'Divisas: USD, EUR, COP con tasas de cambio',
      'Lineas de Negocio (LOBs): Sector > Vertical > Producto',
      'Impuestos: IVA 19%, retencion, etc.',
      'Paises donde opera la empresa',
    ],
    outcome: 'Catalogo base listo para ser usado en modelos y cotizaciones',
  },
  {
    step: 3,
    title: 'Crear usuarios y organizaciones',
    description:
      'El admin configura la estructura organizacional y agrega vendedores y aprobadores.',
    role: 'company_admin',
    icon: Users,
    module: '/users',
    detail: [
      'Crear organizaciones/unidades de negocio',
      'Crear vendedores (rol SALESPERSON)',
      'Crear aprobadores (rol APPROVER)',
      'Asignar usuarios a organizaciones',
      'Configurar permisos por rol',
    ],
    outcome: 'Equipo configurado con estructura organizacional definida',
  },
  {
    step: 4,
    title: 'Disenar modelos de pricing',
    description:
      'Se crean las plantillas de calculo con variables y formulas que definen como se arma un precio.',
    role: 'company_admin',
    icon: DollarSign,
    module: '/pricing-models',
    detail: [
      'Definir variables de entrada (costo materia prima, mano de obra, etc.)',
      'Crear formulas calculadas: precio = costo * (1 + margen)',
      'Configurar margen objetivo (ej: 25%) y margen piso (ej: 15%)',
      'El motor valida dependencias y detecta referencias circulares',
    ],
    outcome: 'Modelo listo con reglas de calculo y umbrales de margen',
  },
  {
    step: 5,
    title: 'Registrar materiales y clientes',
    description: 'Se cargan los materiales/productos y la base de clientes.',
    role: 'company_admin',
    icon: Package,
    module: '/materials + /customers',
    detail: [
      'Codigo interno y codigo de proveedor',
      'Partida arancelaria e historial de tarifas',
      'Clientes con contactos y segmentacion',
    ],
    outcome: 'Catalogo de materiales y clientes disponible para cotizaciones',
  },
  {
    step: 6,
    title: 'Crear una cotizacion',
    description:
      'El vendedor arma una cotizacion para un cliente seleccionando un modelo de pricing y completando los valores.',
    role: 'salesperson',
    icon: FileText,
    module: '/quotes/new',
    detail: [
      'Selecciona cliente, modelo, divisa, pais y LOB',
      'Completa las variables del modelo (costos, cantidades)',
      'Agrega lineas de detalle con materiales',
      'Selecciona impuestos aplicables',
    ],
    outcome: 'Cotizacion en borrador con todos los datos ingresados',
  },
  {
    step: 7,
    title: 'Calculo automatico',
    description:
      'El sistema ejecuta las formulas del modelo y calcula el precio sugerido, margen y impuestos en tiempo real.',
    role: 'system',
    icon: Calculator,
    module: 'Automatico',
    detail: [
      'Evalua formulas en orden topologico (resolviendo dependencias)',
      'Calcula: Costo Base > Margen > Subtotal > Impuestos > Precio Final',
      'Muestra el Cost Waterfall (cascada de costos paso a paso)',
      'Indicador de margen con semaforo: verde / naranja / rojo',
    ],
    branch: {
      condition: 'El sistema compara el margen real vs los umbrales del modelo',
      yes: 'Margen >= Target (25%): Aprobacion automatica',
      no: 'Margen < Target: Requiere aprobacion manual',
    },
  },
  {
    step: 8,
    title: 'Flujo de aprobacion',
    description:
      'Si el margen esta por debajo del objetivo, la cotizacion entra en cola de aprobacion.',
    role: 'approver',
    icon: CheckSquare,
    module: '/approvals',
    detail: [
      'El aprobador ve las cotizaciones pendientes en su cola',
      'Revisa el detalle: cliente, montos, margen, waterfall',
      'Tres opciones: Aprobar / Rechazar / Solicitar Ajuste',
      'Si solicita ajuste, el vendedor corrige y reenvia',
    ],
    outcome: 'Cotizacion aprobada lista para entregar, o rechazada con motivo',
  },
  {
    step: 9,
    title: 'Cotizacion finalizada',
    description:
      'La cotizacion aprobada queda disponible para enviar al cliente. El ciclo puede repetirse.',
    role: 'salesperson',
    icon: Sparkles,
    module: '/quotes/:id',
    detail: [
      'Estado final: APPROVED (lista para enviar)',
      'Historial completo de aprobaciones y comentarios',
      'El vendedor puede ver y compartir la cotizacion',
      'Proxima fase: Generar PDF con hash inmutable',
    ],
    outcome: 'Precio validado y aprobado, listo para el cliente',
  },
  {
    step: 10,
    title: 'Herramientas de IA disponibles',
    description:
      'En cualquier momento, los usuarios pueden usar las herramientas de IA integradas en la plataforma.',
    role: 'anyone',
    icon: Bot,
    module: 'Chat + AI Intelligence + Tour',
    detail: [
      'Asistente IA: chat con tool use para consultar datos reales',
      'AI Intelligence: insights, predicciones y recomendaciones',
      'AI Productividad: metricas de ahorro por usar IA',
      'Tour Guiado: onboarding interactivo paso a paso',
      'Pricing Dinamico: precios automaticos vs competencia',
      'Importaciones: tracking de ordenes y contenedores',
      'SAP Integration: sync automatico de materiales y CRM',
    ],
  },
];

// --- User Flow Components ---

function FlowStepCard({ flowStep, isLast }: { flowStep: FlowStep; isLast: boolean }) {
  const role = roleConfig[flowStep.role];
  const Icon = flowStep.icon;

  return (
    <div className="flex gap-4 md:gap-6">
      {/* Step number + line */}
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'w-10 h-10 rounded-full border-2 flex items-center justify-center shrink-0 font-bold text-sm',
            role.border,
            role.bg,
            role.color
          )}
        >
          {flowStep.step}
        </div>
        {!isLast && (
          <div className="w-0.5 flex-1 min-h-[1rem] bg-border relative">
            <ArrowDown className="w-3 h-3 text-muted-foreground absolute -bottom-1.5 -left-[5px]" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className={cn('flex-1 pb-8 min-w-0')}>
        <Card className={cn('border', role.border)}>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                    role.bg
                  )}
                >
                  <Icon className={cn('w-4 h-4', role.color)} />
                </div>
                <CardTitle className="text-base">{flowStep.title}</CardTitle>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={cn('text-xs', role.color, role.border)}>
                  {role.label}
                </Badge>
                <Badge variant="secondary" className="text-xs font-mono">
                  {flowStep.module}
                </Badge>
              </div>
            </div>
            <CardDescription className="mt-1">{flowStep.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-1.5">
              {flowStep.detail.map((d, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <ArrowRight className={cn('w-3 h-3 mt-1 shrink-0', role.color)} />
                  {d}
                </li>
              ))}
            </ul>

            {/* Branch (decision point) */}
            {flowStep.branch && (
              <div className="mt-3 rounded-lg border border-dashed border-purple-300 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20 p-3 space-y-2">
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-400">
                  {flowStep.branch.condition}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex items-start gap-2 text-xs">
                    <ThumbsUp className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                    <span className="text-green-700 dark:text-green-400">
                      {flowStep.branch.yes}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-xs">
                    <RotateCcw className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                    <span className="text-amber-700 dark:text-amber-400">{flowStep.branch.no}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Outcome */}
            {flowStep.outcome && (
              <div className="flex items-start gap-2 pt-1 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                <span className="text-green-700 dark:text-green-400 font-medium">
                  {flowStep.outcome}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RoleLegend() {
  const roles: { role: FlowRole; icon: any; count: string }[] = [
    { role: 'platform_admin', icon: ShieldCheck, count: 'Paso 1' },
    { role: 'company_admin', icon: UserCog, count: 'Pasos 2-5' },
    { role: 'salesperson', icon: UserCheck, count: 'Pasos 6, 9' },
    { role: 'system', icon: Calculator, count: 'Paso 7' },
    { role: 'approver', icon: CheckSquare, count: 'Paso 8' },
    { role: 'anyone', icon: Bot, count: 'Paso 10' },
  ];

  return (
    <div className="flex flex-wrap gap-3">
      {roles.map(({ role, icon: RIcon, count }) => {
        const config = roleConfig[role];
        return (
          <div
            key={role}
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs',
              config.border,
              config.bg
            )}
          >
            <RIcon className={cn('w-3.5 h-3.5', config.color)} />
            <span className={cn('font-medium', config.color)}>{config.label}</span>
            <span className="text-muted-foreground">({count})</span>
          </div>
        );
      })}
    </div>
  );
}

// --- Page ---

export function ProjectStatusPage() {
  const completedModules = modules.filter((m) => m.status === 'completed').length;
  const partialModules = modules.filter((m) => m.status === 'partial').length;
  const totalModules = modules.length;
  const weightedProgress = Math.round(
    modules.reduce((acc, m) => acc + m.progress, 0) / totalModules
  );
  const completedPhases = phases.filter((p) => p.status === 'completed').length;
  const newModules = modules.filter((m) => m.isNew).length;
  const totalEndpoints = 420;
  const totalDbModels = 94;

  return (
    <AppLayout>
      <div className="container mx-auto p-8 max-w-7xl">
        <div className="space-y-8">
          {/* Header */}
          <FadeIn direction="down">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="mb-3">
                  <EtiquetaDePagina tono="neutro">Mi empresa</EtiquetaDePagina>
                </div>
                <h1 className={CLASE_TITULO_DE_PAGINA}>Estado del Proyecto</h1>
                <p className={`mt-2 ${CLASE_SUBTITULO_DE_PAGINA}`}>
                  Prixia Platform — Alcance, cronograma y avance real
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  <Calendar className="w-3 h-3 mr-1" />
                  Actualizado: 27 Abr 2026
                </Badge>
              </div>
            </div>
          </FadeIn>

          {/* KPI Cards */}
          <StaggerChildren className="grid grid-cols-2 md:grid-cols-5 gap-4" staggerDelay={0.08}>
            <StaggerItem>
              <StatCard
                label="Avance Ponderado"
                value={`${weightedProgress}%`}
                icon={Target}
                accent="text-green-500"
              />
            </StaggerItem>
            <StaggerItem>
              <StatCard
                label="Modulos"
                value={`${completedModules}/${totalModules}`}
                icon={CheckCircle2}
              />
            </StaggerItem>
            <StaggerItem>
              <StatCard
                label="Nuevos Modulos"
                value={newModules}
                icon={Sparkles}
                accent="text-primary"
              />
            </StaggerItem>
            <StaggerItem>
              <StatCard label="API Endpoints" value={`${totalEndpoints}+`} icon={Server} />
            </StaggerItem>
            <StaggerItem>
              <StatCard label="Modelos BD" value={`${totalDbModels}+`} icon={Database} />
            </StaggerItem>
          </StaggerChildren>

          {/* Overall Progress */}
          <FadeIn delay={0.2}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Progreso General</CardTitle>
                <CardDescription>
                  {completedPhases} de {phases.length} fases completadas — {completedModules}{' '}
                  modulos completados, {partialModules} en progreso
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">Avance ponderado por modulo</span>
                    <span className="text-muted-foreground">{weightedProgress}%</span>
                  </div>
                  <ProgressBar value={weightedProgress} size="lg" />
                  <div className="flex justify-between text-xs text-muted-foreground pt-1">
                    <span>Fase 1: Fundamentos</span>
                    <span>Fase 4.7: Tipos de Cambio + Currency Simulator</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </FadeIn>

          {/* New Modules Highlight */}
          <FadeIn delay={0.22}>
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Modulos Nuevos (Feb-Abr 2026)
                </CardTitle>
                <CardDescription>
                  {newModules} modulos agregados en las ultimas iteraciones
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {modules
                    .filter((m) => m.isNew)
                    .map((mod) => {
                      const Icon = mod.icon;
                      const statusConfig = getStatusConfig(mod.status);
                      return (
                        <div
                          key={mod.name}
                          className="flex items-center gap-3 p-3 rounded-lg bg-background border"
                        >
                          <div
                            className={cn(
                              'w-9 h-9 rounded-lg flex items-center justify-center',
                              mod.status === 'completed' ? 'bg-green-500/10' : 'bg-yellow-500/10'
                            )}
                          >
                            <Icon
                              className={cn(
                                'w-4.5 h-4.5',
                                mod.status === 'completed' ? 'text-green-500' : 'text-yellow-500'
                              )}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{mod.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <ProgressBar value={mod.progress} size="sm" />
                              <span className="text-xs text-muted-foreground">{mod.progress}%</span>
                            </div>
                          </div>
                          <Badge variant={statusConfig.variant} className="text-[10px] shrink-0">
                            {statusConfig.label}
                          </Badge>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </FadeIn>

          {/* User Flow */}
          <div>
            <FadeIn delay={0.25}>
              <div className="mb-2">
                <h2 className="text-2xl font-bold">Flujo del Usuario</h2>
                <p className="text-muted-foreground text-sm mt-1 mb-4">
                  Recorrido paso a paso de como se usa la plataforma, desde el registro hasta la
                  cotizacion aprobada
                </p>
                <RoleLegend />
              </div>
            </FadeIn>

            <FadeIn delay={0.28}>
              <div className="mt-6">
                {userFlowSteps.map((fs, i) => (
                  <FlowStepCard
                    key={fs.step}
                    flowStep={fs}
                    isLast={i === userFlowSteps.length - 1}
                  />
                ))}
              </div>
            </FadeIn>
          </div>

          {/* Two column: Timeline + Tech Stack */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Timeline */}
            <FadeIn delay={0.3} className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Cronograma
                  </CardTitle>
                  <CardDescription>Fases de desarrollo del proyecto</CardDescription>
                </CardHeader>
                <CardContent>
                  {phases.map((p, i) => (
                    <TimelineItem key={p.phase} phase={p} isLast={i === phases.length - 1} />
                  ))}
                </CardContent>
              </Card>
            </FadeIn>

            {/* Tech Stack */}
            <FadeIn delay={0.35}>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Server className="w-5 h-5" />
                    Stack Tecnologico
                  </CardTitle>
                  <CardDescription>Tecnologias utilizadas</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {techStack.map((group) => (
                    <div key={group.category}>
                      <p className="text-sm font-medium mb-2">{group.category}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {group.items.map((item) => (
                          <Badge key={item} variant="secondary" className="text-xs font-normal">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </FadeIn>
          </div>

          {/* Modules Grid */}
          <div>
            <FadeIn delay={0.4}>
              <div className="mb-4">
                <h2 className="text-2xl font-bold">Modulos del Sistema</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Detalle de cada modulo con sus funcionalidades — {totalModules} modulos totales,{' '}
                  {newModules} nuevos
                </p>
              </div>
            </FadeIn>

            <StaggerChildren
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
              staggerDelay={0.04}
              initialDelay={0.45}
            >
              {modules.map((mod) => (
                <StaggerItem key={mod.name}>
                  <ModuleCard mod={mod} />
                </StaggerItem>
              ))}
            </StaggerChildren>
          </div>

          {/* Summary Card */}
          <FadeIn delay={0.6}>
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-primary" />
                  Resumen Ejecutivo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                  <div className="space-y-3">
                    <h4 className="font-semibold">Logros principales</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        MVP completo con todos los flujos core de negocio
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        Motor de formulas con 9 tipos de variables y roles MARGIN/TAX/COST/PRICE
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        Smart Pricing con 5 estrategias, simulacion y aplicacion masiva a tiendas
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        Supply chain completo: importaciones, 3D viewer, bin-packing, landed cost,
                        freight legs
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        Tipos de cambio historicos + Currency Simulator + conversion en cadena de
                        precios
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        Demo Store TechStore + Store Connections con push/revert + API v1
                        production-ready
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        94 modelos BD, 420+ endpoints, 18+ modulos nuevos en Feb-Abr
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold">Modulos nuevos (Feb-Abr 2026)</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        Smart Pricing + Reglas + Descuentos con wizards
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        Tipos de Cambio + Currency Simulator + FX en cadena de precios
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        Demo Store (TechStore) full-stack con 3000 productos
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        Store Connections con push de precios e historial reversible
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        Help Center con manual ilustrado y FAQ por modulo
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        API v1 production-ready (cache, rate limit, circuit breaker, Swagger)
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        Support Mode (impersonation), breadcrumbs y busqueda Ctrl+K
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold">Proximos pasos</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        Migracion del Excel Pricer Datec a PricingModels (PRICER_MIGRATION_PLAN.md)
                      </li>
                      <li className="flex items-start gap-2">
                        <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        Hash inmutable en PDFs para verificacion
                      </li>
                      <li className="flex items-start gap-2">
                        <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        Sync bidireccional SAP (push cotizaciones)
                      </li>
                      <li className="flex items-start gap-2">
                        <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        Analiticas avanzadas con forecasting
                      </li>
                      <li className="flex items-start gap-2">
                        <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        Suite de testing (Vitest + Playwright) — Fase 5
                      </li>
                      <li className="flex items-start gap-2">
                        <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        Soft launch con usuarios piloto (May 2026)
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </div>
    </AppLayout>
  );
}

export default ProjectStatusPage;
